import humps from 'humps';
import type { Context } from '@nbx/frontend-helpers/auth';
import { get } from '@nbx/frontend-helpers/fetch-api';

export interface AssetApi {
  id: string;
  type: string;
  name: string;
  status: 'OK' | 'TEST' | string;
  statusDescription: string | null;
  decimalPrecision: number;
  decimalPrecisionDisplay: number;
  decimalPrecisionNetwork: number;
  withdrawalFee: string;
  withdrawalAmountMinimum: string;
  withdrawalConfirmations: number | null;
  depositConfirmations: number | null;
  addressUri: string | null;
  transactionUri: string | null;
  network: string | null;
  disabled: boolean;
}

export type Asset = AssetApi & {
  fiat: boolean;
  pegged: boolean;
  addressFormat: AddressFormat;
  minWithdrawalQuantity: string;
  feePrecision: number;
  maxOrderPrecision: number;
  maxWithdrawalPrecision: number;
  displayPrecision: number;
};

export type AssetsObj = Record<string, Asset>;

export type AddressFormat = 'BTC' | 'ADA' | 'ETH' | 'POL' | string;

export const getAddressFormat = (type: string, assetId: string): AddressFormat => {
  if (type.includes('BTC')) return 'BTC';
  if (type.includes('ADA')) return 'ADA';
  if (type.includes('CARDANO-CNT')) return 'ADA';
  if (type.includes('ETH-ERC20')) return 'ETH';
  if (type.includes('POL-ERC20')) return 'POL';

  return assetId;
};

export const getAssetsBaseUrl = (): string | undefined => window.API_CONFIG?.api.assets?.baseUrl;

export const fetchAssets = (accountContext?: Context): Promise<Array<AssetApi>> =>
  get(`${getAssetsBaseUrl()}/`, accountContext) as Promise<Array<AssetApi>>;

export function formatAssets(assetsInfo: Array<AssetApi>, assetsFromJWT = {}): Record<string, Asset> {
  const formattedAssets = {};
  assetsInfo.forEach(asset => {
    formattedAssets[asset.id] = {
      ...asset,
      // Translate to old data format for compat
      fiat: asset.type === 'FIAT',
      pegged: asset.id === 'USDC',
      addressFormat: getAddressFormat(asset.type, asset.id),
      minWithdrawalQuantity: asset.withdrawalAmountMinimum,
      feePrecision: asset.decimalPrecision,
      maxOrderPrecision: asset.decimalPrecision,
      maxWithdrawalPrecision: asset.decimalPrecision,
      displayPrecision: asset.decimalPrecisionDisplay,
      // Override withdrawal fee with value from JWT
      ...humps.camelizeKeys(assetsFromJWT[asset.id])
    };
  });
  return formattedAssets;
}
